import { memo, useEffect, useMemo, useState } from 'react'
import Grid from '@mui/material/Grid'
import { useNavigate } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'

import TaskList from './TaskList'
import styles from './index.module.scss'
import { MetricCard } from './MetricCard'
import { Scheduler } from '../../scheduler/Scheduler'
import UpcomingWeddingsTable from './UpcomingWeddingsTable'
import WeddingCard from '../../atoms/wedding-card/WeddingCard'
import { getKazoosforStaff } from '../../../features/kazooEventSlice'
import { getVendorStaffEvents } from '../../../features/schedulerSlice'

const VendorStaffScreen = () => {
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const [loading, setLoading] = useState(true)
	const user = useSelector((state) => state.auth.user)
	const kazoos = useSelector((s) => s.kazooEvent.kazoos)
	const partyMembers = [{ Id: user._id, OwnerText: '' }]
	const role = useSelector((state) => state.kazooEvent.role)
	const tasks = useSelector((s) => s.scheduler.timelineEvents)
	const numTasks = useMemo(() => (tasks || []).length, [tasks])
	const numKazoos = useMemo(() => (kazoos || []).length, [kazoos])
	const accountType = useSelector((state) => state.auth.accountType)
	const timelineEvents = useSelector(
		(state) => state.scheduler.timelineEvents
	)

	useEffect(() => {
		const fetchData = async () => {
			if (
				accountType !== 'vendor-staff' &&
				accountType !== 'planner-staff'
			)
				return
			try {
				setLoading(true)
				const promises = [dispatch(getKazoosforStaff())]

				await Promise.all(promises)
			} catch (err) {
				console.error('Error fetching dashboard data:', err)
			} finally {
				setLoading(false)
			}
		}

		fetchData()
	}, [dispatch, accountType])

	useEffect(() => {
		dispatch(getVendorStaffEvents())
	}, [dispatch])

	const upcomingWeddingsRows = useMemo(() => {
		return (kazoos || []).slice(0, 4).map((kazoo) => {
			const budgetLabel =
				typeof kazoo?.budget === 'number'
					? `$${kazoo.budget.toLocaleString()}`
					: 'TBD'

			return {
				id: kazoo?._id,
				Couple: kazoo.kazooName,
				Date: kazoo?.date ? kazoo.date.slice(0, 10) : 'TBD',
				Budget: budgetLabel,
				Status: 'Upcoming',
				avatar: kazoo?.image,
				viewDetails: () => navigate(`/wedding/details/${kazoo._id}`),
			}
		})
	}, [kazoos])

	// Function to handle saving events to the server
	const onActionBegin = async (args) => {
		if (
			args.requestType === 'eventCreate' ||
			args.requestType === 'eventChange' ||
			args.requestType === 'eventRemove'
		) {
			if (role === 'vendor') {
				return toastHelper(
					'You only have view permissions as a vendor FOR NOW',
					'error'
				)
			}
		}
	}

	const eventSettings = {
		allowAdding: false,
		allowEditing: false,
		allowDeleting: false,
		allowResizing: false,
		allowDrag: false,
		dataSource: timelineEvents?.map((event) => ({
			...event,
		})),
	}

	const getLastMonthNumber = () => {
		const today = new Date()
		today.setDate(1)
		today.setMonth(today.getMonth() - 1)

		return today.getMonth()
	}

	const lastMonthNumber = useMemo(() => getLastMonthNumber(), [])
	const overdueTasks = useMemo(
		() => timelineEvents?.filter((event) => event.status === 'Overdue'),
		[timelineEvents]
	)
	const upcomingTasks = useMemo(
		() => timelineEvents?.filter((event) => event.status === 'New'),
		[timelineEvents]
	)
	const activeTasks = useMemo(
		() => timelineEvents?.filter((event) => event.status === 'In Progress'),
		[timelineEvents]
	)
	const lastActiveTasks = useMemo(
		() =>
			activeTasks?.filter(
				(event) =>
					new Date(event.EndTime).getMonth() === lastMonthNumber
			),
		[activeTasks, lastMonthNumber]
	)
	const lastMonthKazoos = useMemo(
		() =>
			kazoos?.filter(
				(event) => new Date(event.date).getMonth() === lastMonthNumber
			),
		[kazoos, lastMonthNumber]
	)
	const completedTasks = useMemo(
		() =>
			timelineEvents?.filter((event) => {
				if (event.status) return event.status === 'Completed'
				else return new Date(event.EndTime) <= new Date()
			}),
		[timelineEvents]
	)
	const lastMonthCompletedTasks = useMemo(
		() =>
			completedTasks?.filter(
				(event) =>
					new Date(event.EndTime).getMonth() === lastMonthNumber
			),
		[completedTasks, lastMonthNumber]
	)
	return (
		<div
			style={{
				paddingBottom: '25px',
			}}
		>
			<div className={styles.pageHeader}>
				<div className={styles.left}>
					<Typography component="h1">Home</Typography>
					{/*<div>
							<Box
								style={{
									border: '1px solid #7CA6BD',
									borderRadius: '8px',
									color: '#7CA6BD',
									padding: '5px 12px',
								backgroundColor: '#F2F6F8',
								fontSize: '12px',
								fontWeight: '600',
							}}
						>
							Lavish Weddings
						</Box>
					</div>*/}
				</div>
			</div>

			<div className={styles.pageMain}>
				<div className={styles.metricCardsWrp}>
					<Grid container spacing={2.5}>
						<Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
							<MetricCard
								label="Total Weddings"
								value={numKazoos ?? 0}
								change={`${lastMonthKazoos?.length > 0 ? `${lastMonthKazoos?.length > 1 ? '+' + (lastMonthKazoos?.length - 1) : '1'} from last month` : ''}`}
								icon="/images/lovely.svg"
								bgColor="#fff"
								changeColor="#4CAF50"
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
							<MetricCard
								label="Total Tasks"
								value={numTasks ?? 0}
								change={`${overdueTasks?.length > 0 ? `${overdueTasks?.length} overdue` : ''}`}
								icon="/images/task-square.svg"
								bgColor="#fff"
								changeColor="#F44336"
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
							<MetricCard
								label="Active Tasks"
								value={activeTasks?.length ?? 0}
								change={`${lastActiveTasks?.length > 0 ? `${lastActiveTasks?.length > 1 ? '+' + (lastActiveTasks?.length - 1) : '1'} from last month` : ''}`}
								icon="/images/timer.svg"
								bgColor="#fff"
								changeColor="#4CAF50"
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
							<MetricCard
								label="Completed Tasks"
								value={completedTasks.length ?? 0}
								change={`${lastMonthCompletedTasks?.length > 0 ? `${lastMonthCompletedTasks?.length > 1 ? '+' + (lastMonthCompletedTasks?.length - 1) : '1'} from last month` : ''}`}
								icon="/images/completed-task.svg"
								bgColor="#fff"
								changeColor="#4CAF50"
							/>
						</Grid>
					</Grid>
				</div>
				<div className={styles.staffCardsWrp}>
					<Grid container spacing={2.5}>
						<Grid size={{ xs: 12, md: 6 }}>
							<WeddingCard
								heading="Upcoming Weddings"
								actionLabel="View All"
								handleAction={() =>
									navigate(`/vendor-staff/weddings`)
								}
							>
								<UpcomingWeddingsTable
									rows={upcomingWeddingsRows}
								/>
							</WeddingCard>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<WeddingCard
								heading="Upcoming Tasks"
								actionLabel="View All Tasks"
								handleAction={() =>
									navigate(`/vendor-staff/tasks`)
								}
							>
								<TaskList timelineEvents={upcomingTasks} />
							</WeddingCard>
						</Grid>
					</Grid>
				</div>
				<div className={styles.schedulerWrap}>
					<div className="calendarWrp commonCalendar ">
						<div className="calendarInner">
							<Scheduler
								disableWeddingDateButton={true}
								onActionBegin={onActionBegin}
								eventSettings={eventSettings}
								partyMembers={partyMembers}
								// showTitle={'Schedule'}
								allowDragAndDrop={false}
								allowResizing={false}
								title="Schedule"
								height={'70vh'}
								isStaff={true}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default memo(VendorStaffScreen)
